import { useCallback, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import {
  TerraDraw,
  TerraDrawCircleMode,
  TerraDrawFreehandMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawSelectMode,
  TerraDrawSessionUndoRedo,
  type GeoJSONStoreFeatures,
  type HexColor,
  type TerraDrawExtend,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";

type FeatureId = TerraDrawExtend.FeatureId;

/**
 * Guidance geometry (closing points, midpoints, selection handles) is always
 * point-based, so a polygon is by definition an area the user drew.
 */
const isDrawnArea = (feature: GeoJSONStoreFeatures) => feature.geometry.type === "Polygon";

// Minimum pointer travel (px) before freehand adds a vertex. Higher = fewer
// vertices to re-render each frame, keeping freehand smooth on dense maps.
// (default = 20.)
const FREEHAND_MIN_DISTANCE_PX = 35;

/**
 * The drawing tools we expose in the UI. These strings intentionally match the
 * `mode` property of the corresponding Terra Draw mode instances, so they can be
 * passed straight to `TerraDraw.setMode`.
 */
export type TerraDrawMode = "select" | "circle" | "polygon" | "rectangle" | "freehand";

export type UseTerraDrawParams = {
  /** Map id, forwarded to `useMap` so we bind to the correct map instance. */
  mapId?: string;
  /**
   * Called whenever the set of drawn features changes (draw, edit, delete,
   * undo, redo, clear). Receives a snapshot copy of every feature currently
   * in the store as GeoJSON. Fires continuously while drawing, so treat this
   * as a preview signal and debounce any expensive work.
   */
  onChange?: (features: GeoJSONStoreFeatures[]) => void;
  /**
   * Called when the user finishes an interaction rather than while they are
   * mid-gesture: completing a shape, releasing a drag/resize, undo, redo and
   * clear. This is the signal to commit the drawing somewhere expensive.
   */
  onCommit?: (features: GeoJSONStoreFeatures[]) => void;
  /**
   * Restrict the map to a single drawn area. Starting a new shape removes the
   * previous one, which keeps the drawn area unambiguous and avoids shapes
   * being stacked on top of each other. Defaults to true.
   */
  singleShape?: boolean;
};

export type UseTerraDrawReturn = {
  /** True once Terra Draw has started and is bound to the map. */
  ready: boolean;
  /** The currently active drawing mode. */
  mode: TerraDrawMode;
  setMode: (mode: TerraDrawMode) => void;
  undo: () => void;
  redo: () => void;
  clear: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** True when at least one feature is drawn. */
  hasFeatures: boolean;
};

/**
 * useTerraDraw
 *
 * Wraps a Terra Draw instance backed by the Google Maps adapter. The hook owns
 * the full lifecycle: it creates the instance once the map + Maps JS API are
 * ready, starts it after the map projection is available, and tears everything
 * down on unmount. Consumers get a small imperative surface (mode switching,
 * undo/redo/clear) plus reactive flags for wiring up toolbar button states.
 *
 * Terra Draw provides native session-level undo/redo, so we rely on that rather
 * than maintaining our own snapshot history.
 */
export function useTerraDraw({
  mapId,
  onChange,
  onCommit,
  singleShape = true,
}: UseTerraDrawParams = {}): UseTerraDrawReturn {
  const map = useMap(mapId);
  const mapsLib = useMapsLibrary("maps");
  const theme = useTheme();

  const drawRef = useRef<TerraDraw | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const singleShapeRef = useRef(singleShape);
  singleShapeRef.current = singleShape;

  const [ready, setReady] = useState(false);
  const [mode, setModeState] = useState<TerraDrawMode>("select");
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [hasFeatures, setHasFeatures] = useState(false);

  useEffect(() => {
    if (!map || !mapsLib) return undefined;

    const color = theme.palette.primary.main as HexColor;
    const fillOpacity = 0.15;
    const outlineWidth = 2;

    const polygonStyles = {
      fillColor: color,
      fillOpacity,
      outlineColor: color,
      outlineWidth,
    };

    const draw = new TerraDraw({
      adapter: new TerraDrawGoogleMapsAdapter({ lib: google.maps, map }),
      // Session-level undo/redo gives us full history across draw/edit/delete.
      undoRedo: { sessionLevel: new TerraDrawSessionUndoRedo() },
      modes: [
        // Select mode lets users move, resize and snap existing shapes, which is
        // what fixes the "areas couldn't reliably be edited" complaint.
        new TerraDrawSelectMode({
          flags: {
            circle: {
              feature: {
                draggable: true,
                coordinates: { midpoints: true, draggable: true, deletable: false },
              },
            },
            polygon: {
              feature: {
                draggable: true,
                coordinates: {
                  midpoints: true,
                  draggable: true,
                  deletable: true,
                  snappable: { toCoordinate: true },
                },
              },
            },
            rectangle: {
              feature: {
                draggable: true,
                coordinates: { midpoints: false, draggable: true, deletable: false },
              },
            },
            freehand: {
              feature: {
                draggable: true,
                coordinates: { midpoints: false, draggable: true, deletable: true },
              },
            },
          },
        }),
        new TerraDrawCircleMode({ styles: polygonStyles }),
        // Snapping to existing coordinates keeps polygons tidy and avoids the
        // "drawing on top of drawings" problem from the old system.
        new TerraDrawPolygonMode({
          styles: polygonStyles,
          snapping: { toCoordinate: true },
        }),
        new TerraDrawRectangleMode({ styles: polygonStyles }),
        // The lasso draws while the mouse button is held rather than trailing the
        // cursor after a click, which is what people expect from a lasso.
        new TerraDrawFreehandMode({
          styles: polygonStyles,
          minDistance: FREEHAND_MIN_DISTANCE_PX,
          drawInteraction: "click-drag",
        }),
      ],
    });

    drawRef.current = draw;

    // `getSnapshot()` deep-clones every feature, so a caller that already has a
    // snapshot passes it in to avoid cloning the store twice.
    const syncFlags = (snapshot?: GeoJSONStoreFeatures[]) => {
      setCanUndo(draw.canUndo());
      setCanRedo(draw.canRedo());
      setHasFeatures((snapshot ?? draw.getSnapshot()).length > 0);
    };

    // Drops every drawn area except the one that was just created, so a new
    // drawing replaces the previous one instead of adding to it.
    const removeSupersededShapes = (createdIds: FeatureId[]) => {
      const created = new Set<FeatureId>(createdIds);
      const areaIds = draw
        .getSnapshot()
        .filter(isDrawnArea)
        .map((feature) => feature.id)
        .filter((id): id is FeatureId => id !== undefined);

      // Selecting a shape also creates features (its drag handles), so only act
      // once a new area itself appears, otherwise we would delete what the user
      // just selected.
      if (!areaIds.some((id) => created.has(id))) return;

      const superseded = areaIds.filter((id) => !created.has(id));
      if (superseded.length) draw.removeFeatures(superseded);
    };

    const handleChange = (ids: FeatureId[], type: string) => {
      if (singleShapeRef.current && type === "create") removeSupersededShapes(ids);

      // `change` fires on every pointer move, so take one snapshot and share it
      // rather than deep-cloning the whole store twice per frame.
      const snapshot = draw.getSnapshot();
      syncFlags(snapshot);
      onChangeRef.current?.(snapshot);
    };

    const handleFinish = () => {
      onCommitRef.current?.(draw.getSnapshot());
    };

    const handleHistory = (event: { cause: string }) => {
      syncFlags();
      // Undo and redo are completed actions in their own right, so they commit
      // too — otherwise the committed result would drift from what is on screen.
      if (event.cause === "undo" || event.cause === "redo") {
        onCommitRef.current?.(draw.getSnapshot());
      }
    };

    draw.on("change", handleChange);
    draw.on("finish", handleFinish);
    draw.on("history", handleHistory);

    const startDrawing = () => {
      if (drawRef.current !== draw) return;
      draw.start();
      draw.setMode("select");
      setReady(true);
      syncFlags();
    };

    // Terra Draw needs the map projection to be available before it can start.
    let projectionListener: google.maps.MapsEventListener | undefined;
    if (map.getProjection()) {
      startDrawing();
    } else {
      projectionListener = google.maps.event.addListenerOnce(
        map,
        "projection_changed",
        startDrawing
      );
    }

    return () => {
      projectionListener?.remove();
      draw.off("change", handleChange);
      draw.off("finish", handleFinish);
      draw.off("history", handleHistory);
      if (draw.enabled) draw.stop();
      drawRef.current = null;
      setReady(false);
    };
    // Theme colour is read once at creation time; we intentionally don't rebuild
    // the whole instance when the theme object identity changes.
  }, [map, mapsLib]);

  const setMode = useCallback((next: TerraDrawMode) => {
    const draw = drawRef.current;
    if (!draw || !draw.enabled) return;
    draw.setMode(next);
    setModeState(next);
  }, []);

  const undo = useCallback(() => {
    drawRef.current?.undo();
  }, []);

  const redo = useCallback(() => {
    drawRef.current?.redo();
  }, []);

  const clear = useCallback(() => {
    const draw = drawRef.current;
    if (!draw || !draw.enabled) return;
    draw.clear();
    draw.clearUndoRedoHistory();
    draw.setMode("select");
    setModeState("select");
    setCanUndo(false);
    setCanRedo(false);
    setHasFeatures(false);
    onChangeRef.current?.([]);
    onCommitRef.current?.([]);
  }, []);

  return { ready, mode, setMode, undo, redo, clear, canUndo, canRedo, hasFeatures };
}
